import React from 'react';

import { Box, PancakeStack } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';

import Footer from './footer/Footer';
import Sidebar from './sidebar/Sidebar';

const MOBILE_HEADER_HEIGHT = '64px';
const SIDEBAR_WIDTH = '245px';

interface ApplicationLayoutProps {
  pageTitle: string;
}

/**
 * ApplicationLayout component provides the standard B4B layout structure
 * with sidebar navigation and content area.
 *
 * Usage:
 * <ApplicationLayout pageTitle="My Page">
 *   <ApplicationLayout.Header>
 *     <H1>Page Title</H1>
 *   </ApplicationLayout.Header>
 *   <ApplicationLayout.Content>
 *     Page content here
 *   </ApplicationLayout.Content>
 *   <ApplicationLayout.Footer />
 * </ApplicationLayout>
 */
const Layout: React.FC<React.PropsWithChildren<ApplicationLayoutProps>> = ({ pageTitle, children }) => {
  const employerId = useCurrentEmployerId();

  return (
    <Box
      css={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
      }}
    >
      <Helmet titleTemplate="%s | Betterment at Work" defaultTitle="Betterment at Work">
        <title>{pageTitle}</title>
      </Helmet>

      {/* Sidebar */}
      <Sidebar employerId={employerId} />

      {/* Main content area */}
      <PancakeStack
        background="background"
        maxWidth="none"
        width="100%"
        height="100%"
        overflowY="auto"
        css={{
          // Account for mobile header height
          marginTop: { base: MOBILE_HEADER_HEIGHT, lg: 0 },
          // On desktop, account for sidebar width
          width: {
            base: '100%',
            lg: `calc(100% - ${SIDEBAR_WIDTH})`,
          },
        }}
      >
        {children}
      </PancakeStack>
    </Box>
  );
};

const Header: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PancakeStack.Header maxWidth="auto" width="100%" backgroundColor="background.surface" p="space.300">
    <Box css={{ maxWidth: '1200px', mx: 'auto' }}>{children}</Box>
  </PancakeStack.Header>
);

const Content: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PancakeStack.Main maxWidth="auto" width="100%" p="space.300">
    <Box css={{ maxWidth: '1200px', mx: 'auto' }}>{children}</Box>
  </PancakeStack.Main>
);

const LayoutFooter: React.FC = () => (
  <PancakeStack.Footer>
    <Footer />
  </PancakeStack.Footer>
);

type ApplicationLayoutComposite = typeof Layout & {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof LayoutFooter;
};

export const ApplicationLayout = Layout as ApplicationLayoutComposite;
ApplicationLayout.Header = Header;
ApplicationLayout.Content = Content;
ApplicationLayout.Footer = LayoutFooter;
